const EXTERNAL_MARKDOWN_SOURCES = new Set(['api', 'frontend']);

export function getDocumentSourcePath(entry) {
  return entry?.sourcePath ?? entry?.path ?? null;
}

export function getSourceLabel(source) {
  if (source === 'api') return 'API';
  if (source === 'frontend') return 'Frontend';
  if (source === 'docs') return 'Docs';
  return source ?? 'Ukjent';
}

export function splitDocsPathHash(value) {
  const [path, ...hashParts] = String(value ?? '').split('#');
  return {
    path,
    hash: hashParts.length > 0 ? `#${hashParts.join('#')}` : ''
  };
}

function normalizePublicDocsPath(sourcePath) {
  const { path, hash } = splitDocsPathHash(sourcePath);
  if (path.startsWith('public/docs/')) return { path, hash };
  return { path: null, hash };
}

function manifestContainsPortalPath(externalManifest, portalPath) {
  if (!externalManifest) return true;
  const normalizedPortalPath = splitDocsPathHash(portalPath).path;
  return (externalManifest.mappings ?? []).some((mapping) => mapping.portalPath === normalizedPortalPath);
}

export function resolveDocumentPortalPath(entry, externalManifest) {
  const source = entry?.source;
  const sourcePath = getDocumentSourcePath(entry);
  if (!source || !sourcePath) return null;

  if (source === 'docs') {
    const { path, hash } = splitDocsPathHash(sourcePath);
    if (path.startsWith('/docs/')) return `${path}${hash}`;
    if (path.startsWith('public/docs/')) return `/${path.replace(/^public\//, '')}${hash}`;
    return null;
  }

  if (!EXTERNAL_MARKDOWN_SOURCES.has(source)) return null;

  const { path, hash } = normalizePublicDocsPath(sourcePath);
  if (!path) return null;

  const relativeDocsPath = path.replace(/^public\/docs\//, '');
  const portalPath = `/docs/external/${source}/${relativeDocsPath}`;
  if (!manifestContainsPortalPath(externalManifest, portalPath)) return null;
  return `${portalPath}${hash}`;
}

export function resolveDocumentPortalStatus(entry, externalManifest) {
  const portalPath = resolveDocumentPortalPath(entry, externalManifest);
  const sourceLabel = getSourceLabel(entry?.source);

  if (portalPath) {
    return {
      portalPath,
      sourceLabel,
      unavailableReason: null
    };
  }

  const sourcePath = getDocumentSourcePath(entry);
  const externalReason = EXTERNAL_MARKDOWN_SOURCES.has(entry?.source)
    ? `${sourceLabel}-dokumentet er ikke pakket i docs-portalen ennå (${sourcePath}).`
    : `${sourceLabel}-dokumentet kan ikke åpnes i docs-portalen (${sourcePath}).`;

  return {
    portalPath: null,
    sourceLabel,
    unavailableReason: externalReason
  };
}

export function getExternalFolder(sourcePath) {
  const normalized = normalizePublicDocsPath(sourcePath);
  if (!normalized.path) return 'core';
  return normalized.path.replace(/^public\/docs\//, '').split('/')[0] || 'core';
}
